import { z } from "zod";

import type { GatewayFrameListing } from "./gatewayFrames";
import type {
  DragonTile,
  DragonTileAttentionDetail,
  DragonTileConfig,
  FrameEventTag,
  InnerTile,
  InnerTileConfig,
  InnerTileFrameRef,
} from "@/types/dragon-tiles";

import { DRAGON_TILE_ATTENTION_REASONS } from "@/types/dragon-tiles";

export interface GatewayDragonTile {
  id: string;
  rj_id?: string;
  site_id: number;
  rj_site_id?: string;
  name: string;
  slug: string;
  order: number;
  category: string;
  type: string;
  is_active: boolean | null;
  is_activatable: boolean | null;
  is_clickable: boolean | null;
  analog_sequence: boolean | null;
  config: DragonTileConfig | null;
  create_date: string | null;
  modify_date: string | null;
  deleted_date: string | null;
  modified_by: string;
  modified_by_email: string;
  modified_by_name: string;
  needs_attention?: unknown;
}

export interface GatewayInnerTile {
  id: string;
  rj_id?: string;
  dragon_tile_id: string;
  rj_dragon_tile_id?: string;
  name: string;
  slug: string;
  order: number;
  category: string;
  is_active: boolean | null;
  config: InnerTileConfig | null;
  audience_id: string;
  create_date: string | null;
  modify_date: string | null;
  deleted_date: string | null;
  modified_by: string;
  modified_by_email: string;
  modified_by_name: string;
}

export interface GatewayDragonTilesResponse {
  dragon_tiles: GatewayDragonTile[];
}

export interface GatewayInnerTilesResponse {
  inner_tiles: GatewayInnerTile[];
}

export type GatewayFrameRef = Partial<
  Pick<
    GatewayFrameListing,
    "id" | "rj_id" | "inner_tile_id" | "rj_inner_tile_id"
  >
> &
  Pick<GatewayFrameListing, "name"> & {
    is_active?: boolean | null;
    metadata?: GatewayFrameListing["metadata"];
  };

export function fromGatewayInnerTile(
  tile: GatewayInnerTile,
  frames: InnerTileFrameRef[],
): InnerTile {
  return {
    id: tile.rj_id || tile.id,
    dragonTileId: tile.rj_dragon_tile_id || tile.dragon_tile_id,
    name: tile.name,
    description: "",
    is_active: tile.is_active ?? true,
    order: tile.order,
    category: tile.category,
    messages: [],
    audience_id: tile.audience_id,
    config: tile.config ?? {},
    frames,
    createdAt: tile.create_date ?? "",
    updatedAt: tile.modify_date ?? "",
    deletedAt: tile.deleted_date,
  };
}

function dragonTileType(
  type: string,
): Pick<DragonTile, "type"> | Record<never, never> {
  if (type === "basic" || type === "connector" || type === "byoc") {
    return { type };
  }
  return {};
}

const attentionEntitiesSchema = z
  .object({
    all: z.boolean(),
    names: z.array(z.string()),
    total: z.number(),
  })
  .nullable()
  .catch(null);

const attentionDetailSchema = z.object({
  reason: z
    .enum(DRAGON_TILE_ATTENTION_REASONS)
    .catch("DRAGON_TILE_ATTENTION_REASON_UNSPECIFIED"),
  active_audiences: attentionEntitiesSchema,
  inactive_audiences: attentionEntitiesSchema,
  active_frames: attentionEntitiesSchema,
  inactive_frames: attentionEntitiesSchema,
});

const needsAttentionSchema = z.array(z.unknown()).catch([]);

function parseNeedsAttention(raw: unknown): DragonTileAttentionDetail[] {
  return needsAttentionSchema.parse(raw ?? []).flatMap((item) => {
    const detail = attentionDetailSchema.safeParse(item);
    return detail.success ? [detail.data] : [];
  });
}

export function fromGatewayDragonTile(
  tile: GatewayDragonTile,
  innerTiles: InnerTile[],
): DragonTile {
  return {
    id: tile.rj_id || tile.id,
    site_id: tile.site_id,
    name: tile.name,
    order: tile.order,
    category: tile.category,
    is_active: tile.is_active ?? true,
    is_activatable: tile.is_activatable ?? true,
    is_clickable: tile.is_clickable ?? true,
    analog_sequence: tile.analog_sequence ?? false,
    ...dragonTileType(tile.type),
    config: tile.config ?? {},
    create_date: tile.create_date ?? "",
    modify_date: tile.modify_date ?? "",
    deleted_date: tile.deleted_date,
    inner_tiles: innerTiles,
    slug: tile.slug,
    needs_attention: parseNeedsAttention(tile.needs_attention),
  };
}

const frameEventTagSchema = z.object({
  name: z.string(),
  active: z.number(),
});

const frameEventTagsSchema = z.array(z.unknown()).catch([]);

export function parseFrameEventTags(raw: unknown): FrameEventTag[] {
  return frameEventTagsSchema.parse(raw ?? []).flatMap((item) => {
    const tag = frameEventTagSchema.safeParse(item);
    return tag.success ? [tag.data] : [];
  });
}

const frameIsActiveSchema = z.boolean().nullable().catch(null);

export function toInnerTileFrameRef(frame: GatewayFrameRef): InnerTileFrameRef {
  return {
    id: frame.rj_id || frame.id || "",
    name: frame.name,
    is_active: frameIsActiveSchema.parse(frame.is_active ?? null),
    events: parseFrameEventTags(frame.metadata?.events),
  };
}

export function groupFrameRefsByInnerTile(
  frames: GatewayFrameRef[],
): Map<string, InnerTileFrameRef[]> {
  const refsByInnerTile = new Map<string, InnerTileFrameRef[]>();
  for (const frame of frames) {
    const innerTileId = frame.rj_inner_tile_id || frame.inner_tile_id;
    if (!innerTileId) continue;
    const refs = refsByInnerTile.get(innerTileId) ?? [];
    refs.push(toInnerTileFrameRef(frame));
    refsByInnerTile.set(innerTileId, refs);
  }
  return refsByInnerTile;
}

export function composeDragonTiles(
  tiles: GatewayDragonTile[],
  innerTiles: GatewayInnerTile[],
  frames: GatewayFrameRef[],
): DragonTile[] {
  const refsByInnerTile = groupFrameRefsByInnerTile(frames);

  // rj2 embeds inner_tiles ordered by their `order` column, and several
  // consumers pick inner_tiles[0]; keep that contract client-side.
  const innerByDragonTile = new Map<string, InnerTile[]>();
  const sorted = [...innerTiles].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    // Pre-#228 `id` is a numeric string ("2" < "10"); post-#228 it's a hashid,
    // where Number() is NaN — fall back to a stable lexicographic compare so the
    // tiebreak stays deterministic instead of collapsing to NaN.
    const an = Number(a.id);
    const bn = Number(b.id);
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
    return String(a.id).localeCompare(String(b.id));
  });
  for (const tile of sorted) {
    const innerTileId = tile.rj_id || tile.id;
    const parentId = tile.rj_dragon_tile_id || tile.dragon_tile_id;
    const mapped = fromGatewayInnerTile(
      tile,
      refsByInnerTile.get(innerTileId) ?? [],
    );
    const siblings = innerByDragonTile.get(parentId) ?? [];
    siblings.push(mapped);
    innerByDragonTile.set(parentId, siblings);
  }

  return tiles.map((tile) =>
    fromGatewayDragonTile(
      tile,
      innerByDragonTile.get(tile.rj_id || tile.id) ?? [],
    ),
  );
}
